import { useMemo, type ReactNode } from 'react';
import { GiftSummaryCell } from '@/components/Orders/GiftSummaryCell';
import { Image, Space, Tag, Tooltip, Typography, theme } from 'antd';
import { FilterFilled } from '@ant-design/icons';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { Link } from 'react-router-dom';
import { OrderState } from '@tcr-erp/shared';
import type { OrderChannel, OrderStatusGroup } from '@tcr-erp/shared';
import type { ERPColumnDef } from '@/components/DataTable/ERPTable';
import { CustomerTypeBadge } from '@/components/Customer/CustomerTypeBadge';
import { DateRangePresetPanel } from '@/components/DateRangePresetPicker';
import { formatVND, formatDateTime } from '@/utils/format';
import type { TenantOrder } from '@/mocks/orders';
import { PosReturnScopeTag } from '../components/PosReturnScopeTag';
import { ErpSkuLink, ErpSkuMoreLink, ProductMoreLink } from '@/components/Orders/MoreItemsPopover';
import { textColorFor, useOrderTags } from '@/api/orders/order-tags';
import {
  CHANNEL_GROUP_COLOR,
  CHANNEL_GROUP_ICON,
  ORDER_PAYMENT_LABEL,
  ORDER_PAYMENT_STATE_LABEL,
  ORDER_PAYMENT_STATE_COLOR,
  ORDER_STATUS_GROUP_COLOR,
  ORDER_STATUS_GROUP_LABEL,
  ORDER_STATE_COLOR,
  ORDER_STATE_LABEL,
  resolveChannelGroup,
  resolveOrderChannelLabel,
  CHANNEL_GROUP_OPTIONS,
} from './constants';
import { ROUTES } from '@/constants/routes';
import {
  type OrderFilterKey,
  type OrderFilterPatch,
  resolveOrderDateRange,
} from './filters';
import { getColumnSortOrder, type OrderSortState } from './orders-sort';
import { SearchFilterPanel, SelectFilterPanel } from './OrderColumnFilterPanels';
import { POS_IMAGE_FALLBACK } from '../../POS/utils/imagePlaceholder';
import { useB2BCreditOverLimitCustomers } from '@/hooks/useB2BCreditOverLimitCustomers';
import { OrderAccountingDocumentsCell } from '@/components/ecommerce/orders/OrderAccountingDocumentsCell';
import { DraftCountdown } from '../components/DraftCountdown';
import { isGtPrepaidOrder, resolveGtPaymentTag } from '../components/confirmOrderPrompt';
import { getDeliveryOverdueStatus, getDeliveryWarningBadge } from '../utils/deliveryOverdue';

const { Text } = Typography;

const ORDER_TABLE_LINK_COLOR = '#1677ff';
/** TCR-2456 — số SP / mã ERP hiện trên 1 dòng đơn, phần còn lại gộp thành "+N … khác" (như ECom). */
const PRODUCT_PREVIEW_LIMIT = 2;

interface UseOrderColumnsProps {
  /** Filter ĐÃ applied (đọc từ URL). */
  filters: Record<OrderFilterKey, string>;
  onApplyFilter: (patch: OrderFilterPatch) => void;
  /** Sort đang áp dụng (đọc từ URL) — dùng để vẽ mũi tên trên header cột. */
  sort: OrderSortState;
  /** Bấm mã đơn mở popup xem nhanh; nếu không truyền thì render link chi tiết. */
  onOpenSummary?: (row: TenantOrder) => void;
  /** TCR-2264 — khi đang chọn đơn, tiêu đề cột "Mã đơn hàng" thành huy hiệu mở panel xử lý hàng loạt. */
  orderCodeTitle?: ReactNode;
  /** TCR-2495 — bấm mã ERP mở popup tồn kho của mã đó (như Đơn Ecom). */
  onOpenErpSkuStock?: (sku: string, row: TenantOrder) => void;
}

export function useOrderColumns({ filters, onApplyFilter, sort, onOpenSummary, orderCodeTitle, onOpenErpSkuStock }: UseOrderColumnsProps): ERPColumnDef<TenantOrder>[] {
  const { token } = theme.useToken();
  const { data: orderTagsData = [] } = useOrderTags();
  const tagById = useMemo(
    () => new Map(orderTagsData.map((t) => [t._id, t] as const)),
    [orderTagsData],
  );
  // TCR-1522 — Set các customer_id đang vượt hạn mức công nợ (real-time), gọi
  // 1 LẦN ở đây (component cha), KHÔNG gọi trong callback `render` của cột
  // "Tên khách hàng" bên dưới — vi phạm Rules of Hooks nếu gọi hook React bên
  // trong hàm render lặp theo từng dòng.
  const overLimitCustomerIds = useB2BCreditOverLimitCustomers();

  return useMemo(() => {
    const filterIcon = (active: boolean) => () => (
      <FilterFilled style={{ color: active ? token.colorPrimary : undefined }} />
    );

    // Mỗi cột bind KHOÁ RIÊNG. Trước đây cả "Mã đơn" lẫn "Khách hàng" cùng dùng `search`
    // nên lọc một cột làm sáng phễu cả hai, và mở phễu cột này lại thấy giá trị cột kia.
    const searchFilter = (
      key: Extract<OrderFilterKey, 'order_number' | 'customer'>,
      placeholder: string,
    ) => ({
      filterDropdown: ({ close }: FilterDropdownProps) => (
        <SearchFilterPanel
          filterKey={key}
          value={filters[key]}
          placeholder={placeholder}
          onApply={onApplyFilter}
          close={close}
        />
      ),
      filterIcon: filterIcon(Boolean(filters[key])),
    });

    const selectFilter = (
      key: Extract<OrderFilterKey, 'channel_group' | 'status_group'>,
      options: { label: string; value: string }[],
    ) => ({
      filterDropdown: ({ close }: FilterDropdownProps) => (
        <SelectFilterPanel
          filterKey={key}
          value={filters[key]}
          options={options}
          onApply={onApplyFilter}
          close={close}
        />
      ),
      filterIcon: filterIcon(Boolean(filters[key])),
    });

    // Đọc qua resolveOrderDateRange để panel hiện đúng khoảng đang lọc khi trang
    // đang dùng preset (date_from/date_to lúc đó rỗng).
    const activeRange = resolveOrderDateRange(filters);
    const dateRangeFilter = () => ({
      // Dùng CHUNG panel preset với FilterBar để hai lối lọc ngày không lệch nhau.
      filterDropdown: ({ close }: FilterDropdownProps) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <DateRangePresetPanel
            preset={filters.date_preset}
            from={activeRange.from}
            to={activeRange.to}
            onChange={onApplyFilter}
            onClose={close}
          />
        </div>
      ),
      filterIcon: filterIcon(Boolean(filters.date_preset || activeRange.from || activeRange.to)),
    });

    const renderMoney = (value: number | undefined) => (
      value == null ? <Text type="secondary">—</Text> : <Text>{formatVND(value)}</Text>
    );

    // TCR-2456 — đơn nhiều SP làm mỗi dòng cao gần hết màn hình: chỉ hiện vài dòng
    // đầu kèm "+N … khác", mirror `buildProductSummary`/`buildErpVariantSummary` ở ECom.
    // TCR-2458 — "+N … khác" bấm được, mở popup liệt kê đủ.
    // TCR-2495 — mã ERP (cả mã trong popup "+N mã ERP khác") bấm được, mở popup tồn kho.
    const renderProductSkus = (row: TenantOrder) => {
      const skus = [...new Set((row.items ?? []).map((item) => item.sku.trim()).filter(Boolean))];
      const openStock = onOpenErpSkuStock ? (sku: string) => onOpenErpSkuStock(sku, row) : undefined;
      return skus.length > 0 ? (
        <Space direction="vertical" size={0}>
          {skus.slice(0, PRODUCT_PREVIEW_LIMIT).map((sku) => (
            <Text key={sku} style={{ color: ORDER_TABLE_LINK_COLOR }}>
              {openStock ? <ErpSkuLink sku={sku} onClick={openStock} /> : sku}
            </Text>
          ))}
          <ErpSkuMoreLink skus={skus} previewLimit={PRODUCT_PREVIEW_LIMIT} onSkuClick={openStock} />
        </Space>
      ) : <Text type="secondary">—</Text>;
    };

    const renderProductNames = (row: TenantOrder) => {
      const items = row.items ?? [];
      return items.length > 0 ? (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {items.slice(0, PRODUCT_PREVIEW_LIMIT).map((item, index) => (
            <Space key={`${item.sku}-${index}`} size={6} align="center" style={{ width: '100%' }}>
              <Image
                src={item.image_url || POS_IMAGE_FALLBACK}
                alt={item.product_name}
                width={32}
                height={32}
                preview={false}
                fallback={POS_IMAGE_FALLBACK}
                style={{
                  objectFit: 'cover',
                  borderRadius: 4,
                  flex: 'none',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #d9d9d9',
                }}
              />
              <Tooltip title={item.product_name}>
                <Text
                  ellipsis
                  style={{ color: ORDER_TABLE_LINK_COLOR, maxWidth: 155 }}
                >
                  {item.product_name || '—'}
                </Text>
              </Tooltip>
            </Space>
          ))}
          <ProductMoreLink
            previewLimit={PRODUCT_PREVIEW_LIMIT}
            items={items.map((item, index) => ({
              key: `${item.sku}-${index}`,
              name: item.product_name || '—',
              sku: item.sku,
              quantity: item.qty,
              imageUrl: item.image_url || POS_IMAGE_FALLBACK,
            }))}
          />
        </Space>
      ) : <Text type="secondary">—</Text>;
    };

    return [
      {
        key: 'order_number',
        title: orderCodeTitle ?? 'Mã đơn hàng',
        alwaysVisible: true,
        // Đủ chỗ cho huy hiệu số đơn đã chọn + nhãn + icon lọc trên một dòng (TCR-2264 QA 13/10).
        width: 180,
        dataIndex: 'order_number',
        render: (v: string, r) => {
          const statusGroup = r.status_group && r.status_group !== 'all'
            ? r.status_group as OrderStatusGroup
            : undefined;
          const statusLabel = (statusGroup && ORDER_STATUS_GROUP_LABEL[statusGroup])
            ?? ORDER_STATE_LABEL[r.state]
            ?? r.state;
          const statusColor = (statusGroup && ORDER_STATUS_GROUP_COLOR[statusGroup])
            ?? ORDER_STATE_COLOR[r.state]
            ?? 'default';

          return (
            <Space direction="vertical" size={0}>
              <Space size={4}>
                {onOpenSummary ? (
                  // TCR-2649 — thẻ <a href> thật (không phải Button) để chuột phải có "Mở liên kết
                  // trong thẻ mới", chuột giữa / Ctrl·Cmd·Shift+click để trình duyệt tự mở trang
                  // chi tiết ở tab mới. Click trái thường vẫn mở popup xem nhanh (TCR-1095).
                  <Link
                    to={ROUTES.orders.detail(r._id)}
                    style={{ color: ORDER_TABLE_LINK_COLOR, fontSize: 12 }}
                    onClick={(event) => {
                      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
                      event.preventDefault();
                      onOpenSummary(r);
                    }}
                  >
                    {v}
                  </Link>
                ) : (
                  <Link to={ROUTES.orders.detail(r._id)}>
                    <Text style={{ color: ORDER_TABLE_LINK_COLOR, fontSize: 12 }}>{v}</Text>
                  </Link>
                )}
                <Text copyable={{ text: v }} />
              </Space>
              <Tag color={statusColor}>{statusLabel}</Tag>
              {r.return_info?.pos_return_scope && <PosReturnScopeTag scope={r.return_info.pos_return_scope} />}
              {/* TCR-2129 — đơn có CK khác chính sách / bị kế toán trả lại. */}
              {r.discount_review?.has_deviation && (
                <Tooltip
                  title={`${r.discount_review.deviation_count ?? 0} dòng CK khác chính sách · chênh ${formatVND(r.discount_review.total_deviation_amount ?? 0)}`}
                >
                  <Tag color="orange" style={{ marginInlineStart: 4 }}>Sửa CK</Tag>
                </Tooltip>
              )}
              {r.discount_review?.returned && <Tag color="red">Bị trả lại</Tag>}
              {/* TCR-1963 — đồng hồ đếm ngược tự huỷ, chỉ hiện với đơn Nháp còn hạn. */}
              {r.state === OrderState.DRAFT && <DraftCountdown expiresAt={r.draft_expires_at} />}
              {r.skipped && <Tag color="default">Bỏ qua</Tag>}
              {(r.tags ?? []).map((tagId) => {
                const tag = tagById.get(tagId);
                if (!tag) return null;
                return (
                  <Tag key={tagId} color={tag.color} style={{ marginTop: 2, color: textColorFor(tag.color) }}>
                    {tag.name}
                  </Tag>
                );
              })}
            </Space>
          );
        },
        ...searchFilter('order_number', 'Tìm mã đơn…'),
      },
      {
        key: 'channel',
        title: 'Kênh',
        hideable: true,
        width: 140,
        dataIndex: 'channel',
        render: (v: OrderChannel, row: TenantOrder) => {
          const g = resolveChannelGroup(v);
          const channelTag = <Tag color={CHANNEL_GROUP_COLOR[g]}>{CHANNEL_GROUP_ICON[g]} {resolveOrderChannelLabel(row)}</Tag>;
          // TCR-2130 — đơn kênh GT (khách trả trước) cần quyền duyệt riêng, đánh dấu ngay trên danh sách.
          if (!isGtPrepaidOrder(row)) return channelTag;
          const gtTag = resolveGtPaymentTag(row);
          return (
            <Space direction="vertical" size={4}>
              {channelTag}
              <Tag color={gtTag.color} data-testid="order-row-gt-prepaid-tag">{gtTag.label}</Tag>
            </Space>
          );
        },
        ...selectFilter('channel_group', CHANNEL_GROUP_OPTIONS),
      },
      {
        key: 'created_at',
        title: 'Ngày tạo đơn',
        hideable: true,
        width: 150,
        dataIndex: 'created_at',
        sorter: true,
        sortOrder: getColumnSortOrder('created_at', sort),
        render: (v: string) => v ? formatDateTime(v) : '-',
        ...dateRangeFilter(),
      },
      {
        key: 'erp_sku',
        title: 'Mã ERP',
        hideable: true,
        width: 150,
        render: (_: unknown, row: TenantOrder) => renderProductSkus(row),
      },
      {
        key: 'product_name',
        title: 'Tên sản phẩm',
        hideable: true,
        width: 220,
        render: (_: unknown, row: TenantOrder) => renderProductNames(row),
      },
      {
        // TCR-2625 — đơn có SP thuộc danh mục Gift: tag "🎁 n SP" (n = tổng SL quà) + popover.
        key: 'gift_summary',
        title: 'Quà tặng',
        hideable: true,
        width: 96,
        render: (_: unknown, row: TenantOrder) => <GiftSummaryCell summary={row.gift_summary} />,
      },
      {
        key: 'total_goods_amount',
        title: 'Tổng tiền hàng',
        hideable: true,
        width: 140,
        render: (_: unknown, row: TenantOrder) => renderMoney(row.summary?.original_price),
      },
      {
        key: 'order_revenue',
        title: 'Doanh thu đơn hàng',
        hideable: true,
        width: 150,
        render: (_: unknown, row: TenantOrder) => renderMoney(row.order_revenue),
      },
      {
        key: 'payment_state',
        title: 'Trạng thái thanh toán',
        hideable: true,
        width: 160,
        render: (_: unknown, row: TenantOrder) => row.payment_state
          ? <Tag color={ORDER_PAYMENT_STATE_COLOR[row.payment_state] ?? 'default'}>
            {ORDER_PAYMENT_STATE_LABEL[row.payment_state] ?? row.payment_state}
          </Tag>
          : <Text type="secondary">—</Text>,
      },
      {
        // TCR-2666 — "Chứng từ kế toán" như Đơn Ecom (thay cột "Số hoá đơn" TCR-1967; hoá đơn GTGT
        // vẫn nằm trong cột này kèm tag Thay thế/Điều chỉnh). Giữ key cũ để không mất thiết lập cột đã lưu.
        key: 'e_invoice_numbers',
        title: 'Chứng từ kế toán',
        hideable: true,
        width: 190,
        render: (_: unknown, row: TenantOrder) => <OrderAccountingDocumentsCell order={row} />,
      },
      {
        key: 'discount',
        title: 'Chiết khấu',
        hideable: true,
        width: 160,
        // TCR-1044 (vòng 3, phần còn thiếu của ticket gốc): danh sách đơn chỉ
        // hiện SỐ TIỀN chiết khấu, không có cách nào biết số đó đến từ voucher
        // mã nào / chương trình khuyến mãi tự động nào — phải mở từng đơn mới
        // đối chiếu được. Thêm badge số lượng + tooltip liệt kê mã/tên, cùng
        // màu với khối "Thông tin đơn hàng" (`OrderInfoPanel.tsx`: xanh dương
        // = voucher mã thật, tím = khuyến mãi tự động) để hai màn nhất quán.
        render: (_: unknown, row: TenantOrder) => {
          const vouchers = row.summary?.applied_vouchers ?? [];
          const promotions = row.summary?.applied_promotions ?? [];
          const totalPrograms = vouchers.length + promotions.length;
          if (totalPrograms === 0) return renderMoney(row.discount);
          return (
            <Tooltip
              title={
                <Space direction="vertical" size={2}>
                  {vouchers.map((v, i) => (
                    <div key={`v-${v.voucher_code ?? v.code ?? i}`}>
                      <Tag color="blue" style={{ marginInlineEnd: 4 }}>{v.voucher_code ?? v.code}</Tag>
                      {formatVND(v.discount_amount)}
                    </div>
                  ))}
                  {promotions.map((p, i) => (
                    <div key={`p-${p.promotion_id ?? p.name}-${i}`}>
                      <Tag color="purple" style={{ marginInlineEnd: 4 }}>{p.name}</Tag>
                      {formatVND(p.discount_amount)}
                    </div>
                  ))}
                </Space>
              }
            >
              <Space size={4}>
                {renderMoney(row.discount)}
                <Tag
                  color={vouchers.length > 0 ? 'blue' : 'purple'}
                  style={{ marginInlineEnd: 0, cursor: 'default' }}
                >
                  {totalPrograms}
                </Tag>
              </Space>
            </Tooltip>
          );
        },
      },
      {
        key: 'deposit_amount',
        title: 'Tiền đặt cọc',
        hideable: true,
        width: 130,
        render: (_: unknown, row: TenantOrder) => renderMoney(row.shipping?.prepaid_amount),
      },
      {
        key: 'cod_amount',
        title: 'Tiền COD',
        hideable: true,
        width: 120,
        render: (_: unknown, row: TenantOrder) => renderMoney(row.shipping?.cod_amount),
      },
      {
        key: 'debt_amount',
        title: 'Công nợ',
        hideable: true,
        // Mặc định ẩn: chỉ đơn có ghi nợ mới dùng tới, không đáng chiếm chỗ
        // ngang của mọi người xem danh sách đơn.
        defaultHidden: true,
        width: 120,
        render: (_: unknown, row: TenantOrder) => renderMoney(row.shipping?.debt_amount),
      },
      {
        key: 'payment_method',
        title: 'Hình thức thanh toán',
        hideable: true,
        width: 160,
        render: (_: unknown, row: TenantOrder) => row.payment_method
          ? (ORDER_PAYMENT_LABEL[row.payment_method] ?? row.payment_method)
          : <Text type="secondary">—</Text>,
      },
      {
        key: 'shipping_fee_estimate',
        title: 'Phí vận chuyển ước tính',
        hideable: true,
        width: 170,
        render: (_: unknown, row: TenantOrder) => renderMoney(row.shipping_fee_estimate),
      },
      {
        key: 'shipping_fee_actual',
        title: 'Phí vận chuyển thực tế',
        hideable: true,
        width: 170,
        render: (_: unknown, row: TenantOrder) => renderMoney(row.shipping_fee_actual),
      },
      {
        key: 'customer_name',
        title: 'Tên khách hàng',
        hideable: true,
        width: 180,
        render: (_: unknown, row: TenantOrder) => row.customer?.name ? (
          <Space size={6} wrap>
            <Text>{row.customer.name}</Text>
            <CustomerTypeBadge type={row.user_type ?? (row.customer as { type?: string } | undefined)?.type} />
            {overLimitCustomerIds.has(String(row.user_id ?? '')) && (
              <Tag color="red">Quá công nợ</Tag>
            )}
          </Space>
        ) : '—',
        ...searchFilter('customer', 'Tìm tên hoặc SĐT khách…'),
      },
      {
        key: 'customer_note',
        title: 'Ghi chú khách hàng',
        hideable: true,
        width: 200,
        render: (_: unknown, row: TenantOrder) => row.note
          ? <Tooltip title={row.note}><Text ellipsis style={{ maxWidth: 175 }}>{row.note}</Text></Tooltip>
          : <Text type="secondary">—</Text>,
      },
      {
        // TCR-1221: khác `customer_note` ở trên — ghi chú cho ĐVVC/nội bộ,
        // đọc `order.shipping_note` (top-level, KHÔNG lồng trong `shipping`).
        key: 'shipping_note',
        title: 'Ghi chú vận chuyển',
        hideable: true,
        width: 240,
        // Ghi chú cho ĐVVC phải đọc được TRỌN VẸN ngay trên danh sách (yêu cầu
        // tester vòng 2): phần quyết định việc giao hàng — khung giờ, số điện
        // thoại thay thế, dặn dò — hay nằm ở đuôi câu, cắt bằng "…" là mất đúng
        // phần đó. Cho xuống dòng trong ô thay vì ellipsis 1 hàng.
        render: (_: unknown, row: TenantOrder) => row.shipping_note
          ? (
            <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'inline-block' }}>
              {row.shipping_note}
            </Text>
          )
          : <Text type="secondary">—</Text>,
      },
      {
        key: 'customer_phone',
        title: 'Số điện thoại',
        hideable: true,
        width: 140,
        render: (_: unknown, row: TenantOrder) => row.customer?.phone
          ? <Text style={{ color: ORDER_TABLE_LINK_COLOR }}>{row.customer.phone}</Text>
          : '—',
      },
      {
        key: 'tracking_number',
        title: 'Mã vận đơn',
        hideable: true,
        width: 150,
        render: (_: unknown, row: TenantOrder) => {
          const trackingNumber = row.shipping?.tracking_number ?? row.shipping?.shipping_partner?.order_id;
          return trackingNumber ? (
            <Space size={4}>
              <Text style={{ color: ORDER_TABLE_LINK_COLOR }}>{trackingNumber}</Text>
              <Text copyable={{ text: trackingNumber }} />
            </Space>
          ) : '—';
        },
      },
      {
        key: 'carrier',
        title: 'Đơn vị vận chuyển',
        hideable: true,
        width: 160,
        render: (_: unknown, row: TenantOrder) => {
          const carrier = row.shipping?.carrier ?? row.shipping?.shipping_partner?.name;
          return carrier ? <Text strong>{carrier}</Text> : '—';
        },
      },
      {
        key: 'delivery_time',
        title: 'Thời gian giao hàng',
        hideable: true,
        width: 190,
        render: (_: unknown, row: TenantOrder) => {
          const status = getDeliveryOverdueStatus(row);
          const warningBadge = getDeliveryWarningBadge(status);

          return (
            <Space direction="vertical" size={2}>
              <Text>
                {status.deadline ? formatDateTime(status.deadline.toISOString()) : '—'}
              </Text>
              {warningBadge && (
                <Tag color={warningBadge.color} style={{ marginInlineEnd: 0 }}>
                  {warningBadge.label}
                </Tag>
              )}
            </Space>
          );
        },
      },
      {
        key: 'address',
        title: 'Địa chỉ',
        hideable: true,
        width: 220,
        hideOnMobile: true,
        render: (_: unknown, row: TenantOrder) =>
          [row.shipping?.address?.address, row.shipping_ward_name].filter(Boolean).join(', ') || '—',
      },
      {
        key: 'city',
        title: 'Tỉnh/Thành phố',
        hideable: true,
        width: 160,
        render: (_: unknown, row: TenantOrder) => row.shipping_city_name || '—',
      },
      {
        key: 'created_by_name',
        title: 'Người tạo đơn',
        hideable: true,
        width: 150,
        render: (_: unknown, row: TenantOrder) => row.created_by_name || '—',
      },
      // TCR-1668 — cột "Chi nhánh". `hideable` như các cột phụ khác nên người dùng tự bật/tắt
      // trong "Tuỳ chỉnh cột"; tenant một chi nhánh có thể ẩn đi cho gọn.
      {
        key: 'store_name',
        title: 'Chi nhánh',
        hideable: true,
        width: 180,
        render: (_: unknown, row: TenantOrder) => row.store_name || '—',
      },
    ];
  }, [filters, onApplyFilter, sort, onOpenSummary, token.colorPrimary, tagById, overLimitCustomerIds, orderCodeTitle, onOpenErpSkuStock]);
}
